import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils'
import type {
  LexicalCommand,
  LexicalEditor
} from 'lexical'
import {
  $createParagraphNode,
  $createRangeSelection,
  $getSelection,
  $insertNodes,
  $isNodeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND
} from 'lexical'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { CAN_USE_DOM } from './images-plugin'

import {
  $createInlineImageNode,
  $isInlineImageNode,
  InlineImageNode,
  type InlineImagePayload
} from '../nodes/inline-image-node'
import { DialogActions } from '../ui/dialog'
import UsersLatestUploads from '@/components/user-latest-update'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const getDOMSelection = (targetWindow: Window | null): Selection | null =>
  CAN_USE_DOM ? (targetWindow || window).getSelection() : null

export const INSERT_INLINE_IMAGE_COMMAND: LexicalCommand<InlineImagePayload> = createCommand('INSERT_INLINE_IMAGE_COMMAND')

/**
 * InsertInlineImageDialog
 *
 * NOTE: image uploading is pluggable — pass `uploadFile` prop (file => Promise<src string>)
 * from the application to handle server upload. Plugins should not call mutation directly.
 */
export function InsertInlineImageDialog({ editor, onClose, uploadFile }: { editor: LexicalEditor; onClose: () => void; uploadFile?: (file: File) => Promise<string> }): React.JSX.Element {
  const hasModifier = useRef(false)
  const [src, setSrc] = useState<string | File>('')
  const [altText, setAltText] = useState('')
  const [position, setPosition] = useState<'left' | 'right' | 'full'>('left')

  const isDisabled = src === ''

  const [isUploading, setIsUploading] = useState(false) 

  const loadImage = (files: FileList | null) => {
    if (files !== null && files.length > 0) {
      const file = files[0]
      setSrc(
        new File([file], file.name.replace(/[^a-zA-Z0-9_.]/g, '').replace(/[\s-]/g, ''), {
          type: file.type
        })
      )
    }
  }

  useEffect(() => {
    hasModifier.current = false
    const handler = (e: KeyboardEvent) => {
      hasModifier.current = e.altKey
    }
    document.addEventListener('keydown', handler)
    return () => {
      document.removeEventListener('keydown', handler)
    }
  }, [editor])

  const handleOnClick = async (imageSrc: string | File) => {
    if (typeof imageSrc === 'string') {
      const payload: InlineImagePayload = { altText, position, showCaption: false, src: imageSrc }
      editor.dispatchCommand(INSERT_INLINE_IMAGE_COMMAND, payload)
      onClose()
      return
    }

    try {
      if (!uploadFile) {
        console.error('InsertInlineImageDialog: uploadFile handler is not provided.')
        onClose()
        return
      }
      setIsUploading(true)
      const uploadedSrc = await uploadFile(imageSrc as File)
      setIsUploading(false)
      if (uploadedSrc) {
        editor.dispatchCommand(INSERT_INLINE_IMAGE_COMMAND, {
          src: uploadedSrc,
          altText,
          position,
          showCaption: false
        })
        onClose()
      } else {
        console.error('Upload succeeded but no image path returned', uploadedSrc)
      }
    } catch (error) {
      setIsUploading(false)
      console.error('Failed to upload inline image:', error)
    }
  }

  return (
    <div className='flex flex-col gap-4'>
      <UsersLatestUploads onClick={(upload: any) => handleOnClick(upload?.image_path)} />
      <div>
        <label className='text-sm mb-2 font-medium text-foreground block'>Upload new image</label>
        <input
          type='file'
          accept='image/*'
          className='w-full text-gray-600 text-sm mt-1 file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer'
          onChange={(e) => loadImage(e.target.files)}
          data-test-id='image-modal-file-upload'
        />
      </div>
      <div>
        <label className='text-sm mb-2 font-medium text-foreground block'>Alt Text</label>
        <input
          placeholder='Descriptive alternative text'
          onChange={(e) => setAltText(e.target.value)}
          value={altText}
          data-test-id='image-modal-alt-text-input'
          className='focus:border-blue-500 border-gray-400 bg-white border text-gray-900 text-sm rounded-lg block w-full px-2.5 py-1.5 outline-none'
        />
      </div>
      <div>
        <label className='text-sm mb-2 font-medium text-foreground block'>Position</label>
        <Select value={position} onValueChange={(val: 'left' | 'right' | 'full') => setPosition(val)}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select position" />
          </SelectTrigger>
          <SelectContent className="z-[99999]">
            <SelectItem value="left">Left</SelectItem>
            <SelectItem value="right">Right</SelectItem>
            <SelectItem value="full">Full Width</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DialogActions>
        <Button
          disabled={isDisabled || isUploading}
          size="sm"
          className='ml-auto'
          onClick={() => handleOnClick(src)}
        >
          {isUploading ? 'Uploading...' : 'Confirm'}
        </Button>
      </DialogActions>
    </div >
  )
}

export default function InlineImagePlugin(): null {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    if (!editor.hasNodes([InlineImageNode])) {
      throw new Error('InlineImagePlugin: InlineImageNode not registered on editor')
    }

    return mergeRegister(
      editor.registerCommand(
        INSERT_INLINE_IMAGE_COMMAND,
        (payload: InlineImagePayload) => {
          const inlineImageNode = $createInlineImageNode(payload)
          $insertNodes([inlineImageNode])
          if ($isRootOrShadowRoot(inlineImageNode.getParentOrThrow())) {
            $wrapNodeInElement(inlineImageNode, $createParagraphNode).selectEnd()
          }

          return true
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        (event) => {
          return onDragStart(event)
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        DRAGOVER_COMMAND,
        (event) => {
          return onDragover(event)
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        DROP_COMMAND,
        (event) => {
          return onDrop(event, editor)
        },
        COMMAND_PRIORITY_HIGH
      )
    )
  }, [editor])

  return null
}

const TRANSPARENT_IMAGE =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
const img = CAN_USE_DOM ? document.createElement('img') : {} as HTMLImageElement
if (CAN_USE_DOM) {
  (img as HTMLImageElement).src = TRANSPARENT_IMAGE
}

function onDragStart(event: DragEvent): boolean {
  const node = getInlineImageNodeInSelection()
  if (!node) {
    return false
  }
  const dataTransfer = event.dataTransfer
  if (!dataTransfer) {
    return false
  }
  dataTransfer.setData('text/plain', '_')
  dataTransfer.setDragImage(img as HTMLImageElement, 0, 0)
  dataTransfer.setData(
    'application/x-lexical-drag',
    JSON.stringify({
      data: {
        altText: node.__altText,
        caption: node.__caption,
        height: node.__height,
        key: node.getKey(),
        src: node.__src,
        width: node.__width
      },
      type: 'image'
    })
  )

  return true
}

function onDragover(event: DragEvent): boolean {
  const node = getInlineImageNodeInSelection()
  if (!node) {
    return false
  }
  if (!canDropImage(event)) {
    event.preventDefault()
  }
  return true
}

function onDrop(event: DragEvent, editor: LexicalEditor): boolean {
  const node = getInlineImageNodeInSelection()
  if (!node) {
    return false
  }
  const data = getDragImageData(event)
  if (!data) {
    return false
  }
  event.preventDefault()
  if (canDropImage(event)) {
    const range = getDragSelection(event)
    node.remove()
    const rangeSelection = $createRangeSelection()
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range)
    }
    $setSelection(rangeSelection)
    editor.dispatchCommand(INSERT_INLINE_IMAGE_COMMAND, data)
  }
  return true
}

function getInlineImageNodeInSelection(): InlineImageNode | null {
  const selection = $getSelection()
  if (!$isNodeSelection(selection)) {
    return null
  }
  const nodes = selection.getNodes()
  const node = nodes[0]
  return $isInlineImageNode(node) ? node : null
}

function getDragImageData(event: DragEvent): InlineImagePayload | null {
  const dragData = event.dataTransfer?.getData('application/x-lexical-drag')
  if (!dragData) {
    return null
  }
  const { type, data } = JSON.parse(dragData)
  if (type !== 'image') {
    return null
  }

  return data
}

function canDropImage(event: DragEvent): boolean {
  const target = event.target
  return !!(
    target &&
    target instanceof HTMLElement &&
    !target.closest('code, span.editor-image') &&
    target.parentElement &&
    target.parentElement.closest('div.ContentEditable__root')
  )
}

function getDragSelection(event: DragEvent): Range | null {
  let range: Range | null = null
  const target = event.target as Node
  const targetWindow =
    target == null
      ? null
      : target.nodeType === 9
        ? (target as Document).defaultView
        : target.ownerDocument?.defaultView
  const domSelection = getDOMSelection(targetWindow || null)
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY)
  } else if ((event as any).rangeParent && domSelection !== null) {
    domSelection.collapse((event as any).rangeParent, (event as any).rangeOffset || 0)
    range = domSelection.getRangeAt(0)
  } else {
    throw Error('Cannot get the selection when dragging')
  }

  return range
}
